// HER DASHBOARD ITEM, SURFACE 2(c) — **THE FULL CONVERSION LADDER, ONE HONEST ROW.**
//
// Her words: *visitors, signups, paid, returning, as counts AND percentages for the period,
// RECONCILED: the signups number must MATCH Overview's signups for the same period.*
//
// **IT IS HOOKLESS AND TAKES ITS DATA AS A PROP, SO A GATE CALLS IT** (R7/R12's entry requirement,
// and `OwnerTraffic`'s own shape one section up). The analytics tab already reads ONE payload for
// the whole tab; a fetch here would be a second answer to *what period is this* on a dashboard whose
// whole D6-3 ruling is one range control.
//
// **EVERY NUMBER AND EVERY PERCENTAGE IS THE SHARED LAW'S.** `conversionLadderRows` decides which
// shares may be drawn at all and `ladderCoverageNote` says why one is missing; nothing is re-derived
// here, because a surface that does its own arithmetic is a second answer key (R28/R29) — and on
// this row the second answer would be the one she reads her advertising by.
import { conversionLadderRows, ladderCoverageNote } from "../conversionLadder.js";

export type LadderPayload = {
  visitors: number | null;
  signups: number;
  paid: number;
  returning: number | null;
  coverage: string;
  since: string | null;
};

const short = (n: number) => new Intl.NumberFormat("en-US").format(n);

export function OwnerLadder({ ladder }: { ladder: LadderPayload | null | undefined }) {
  // >>> THREE STATES, NEVER TWO (R18/R65), AND THE MIDDLE ONE IS THE ONE THAT COSTS SOMETHING. <<<
  // `undefined` is a payload still in flight and HOLDS STILL. `null` is a read that ARRIVED AND
  // COULD NOT BE MADE, and it says so — drawing it as four zeroes puts *nobody came, nobody joined,
  // nobody paid* on the summary row of her business on a day the database refused a query.
  if (ladder === undefined) return null;
  if (ladder === null) {
    return (
      <section className="owner-ladder" data-ladder-state="unread">
        <h3 className="owner-detail__sub">The whole journey</h3>
        <p className="owner-page__note">These numbers could not be read just now.</p>
      </section>
    );
  }

  const rows = conversionLadderRows(ladder);
  const note = ladderCoverageNote(ladder.coverage, ladder.since || "");

  return (
    <section className="owner-ladder" data-ladder-state="read">
      <h3 className="owner-detail__sub">The whole journey</h3>
      <div className="owner-ladder__row">
        {rows.map((r) => (
          <div className="owner-ladder__step" key={r.key} data-ladder-step={r.key}>
            {/* A COUNT THAT COULD NOT BE READ IS A DASH AND NEVER A ZERO (R18). The traffic read is
                the one that can refuse, so the two visitor-side steps really reach this branch. */}
            <span className="owner-ladder__n">{r.count === null ? "—" : short(r.count)}</span>
            <span className="owner-ladder__k">{r.label}</span>
            {/* A PERCENTAGE OF NOTHING IS NOTHING, NOT ZERO, and a percentage of a denominator that
                does not cover the window is not drawn at all — the law decides, and it prints what
                the share is OF, so a number on this row can never be a percentage of something she
                has to guess at. */}
            <span className="owner-ladder__pct">
              {r.pct === null ? "" : `${r.pct}% of ${r.of === "visitors" ? "visitors" : "signups"}`}
            </span>
          </div>
        ))}
      </div>
      {/* >>> A WITHHELD PERCENTAGE LOOKS LIKE A BROKEN ONE UNLESS SOMETHING SAYS WHY. <<< The note is
          empty when the window is fully covered, so a screen that has nothing to apologise for says
          nothing (SURFACE 2(b)'s own ruling, one card up). */}
      {note ? <p className="owner-page__note owner-ladder__note">{note}</p> : null}
    </section>
  );
}
